import { Injectable, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import {
	BehaviorSubject,
	filter,
	map,
	merge,
	Observable,
	firstValueFrom
} from 'rxjs';
import {
	AuthConfig,
	OAuthErrorEvent,
	OAuthEvent,
	OAuthService,
	OAuthSuccessEvent,
} from 'angular-oauth2-oidc';
import {
	IOidcProvider,
	IAuthProvider,
	providerTypes,
} from '../models';
import AuthCallback from './authCallback';

// Keycloak Realm Access
export interface RealmAccess {
	roles: string[];
}

// Keycloak Client Roles
export interface ClientRoles {
	roles: string[];
}

// Keycloak Resource Access (Client Rollen)
export interface ResourceAccess {
	[clientId: string]: ClientRoles;
}

export interface IOpenIDClaims extends IOIDAstimaxClaims {
	exp: number
	iat: number
	auth_time: number
	jti: string
	iss: string
	aud: string
	sub: string
	typ: string
	azp: string
	nonce: string
	session_state: string
	acr: string
	"allowed-origins": string[]
	scope: string
	sid: string
	email_verified: boolean
	name: string
	preferred_username: string
	given_name: string
	family_name: string
	email: string

	// Keycloak Rollen-Erweiterungen
	realm_access?: RealmAccess;
	resource_access?: ResourceAccess;
}

interface IOIDAstimaxClaims {
	"astimax-sip-user"?: string;
	"astimax-sip-password"?: string;
	"astimax-registrar"?: string;
	"astimax-vbox"?: string[];
}

interface IRouteHistoryState {
	oAuthUser?: { info: IOpenIDClaims }
}

interface IAuthProviderConfiguration {
	localOauthProvider?: IAuthProvider;
	localProviderEnabled: boolean;
	oauthProviders: IAuthProvider[];
}

interface RetryConfig {
	maxAttemptsForAuthErrors: number;      // Fast fail for 401/403 (token revoked)
	maxAttemptsForConnectionErrors: number; // Patient retries for network/provider issues
	baseDelayMs: number;
	maxDelayMs: number;
	exponentialBase: number;
	currentAttempt: number;
}

// Provider health tracking
interface ProviderHealthState {
	state: 'HEALTHY' | 'DEGRADED' | 'RECOVERING';
	failureCount: number;
	lastFailureTime: number;
	nextAttemptTime: number;
}

/**
 * AuthService - Production-Ready OAuth2/OIDC Authentication
 *
 * Features:
 * - Automatic token refresh under high load (500ms notifications)
 * - Circuit breaker pattern for provider health
 * - Exponential backoff with jitter
 * - Automatic recovery from network/provider outages
 * - Multi-tab synchronization
 * - Session validation with Keycloak
 * - Refresh verification safety net
 * - Scheduled verification check
 */
@Injectable({
	providedIn: 'root'
})
export class AuthService extends AuthCallback {
	private readonly destroyRef: DestroyRef = inject(DestroyRef);

	// Console color constants for better control
	private readonly LOG_COLORS = {
		INFO: '#2196F3',      // Blue - informational messages
		SUCCESS: '#4CAF50',   // Green - successful operations
		WARNING: '#FF9800',   // Orange - warnings
		ERROR: '#F44336',     // Red - errors
		CRITICAL: '#FF5722',  // Deep Orange - critical errors
		DEBUG: '#9C27B0'      // Purple - debug information
	};

	// State management
	private readonly isAuthenticatedSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private readonly isDoneLoadingSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private readonly oauthConfigErrorSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	// Network and connection state
	private readonly isOnlineSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(navigator.onLine);
	private readonly isCheckingConnectionSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	// Retry configuration
	private readonly retryConfig: RetryConfig = {
		maxAttemptsForAuthErrors: 3,        // Fast fail for 401/403: ~7 seconds total
		maxAttemptsForConnectionErrors: 20, // Patient for infrastructure: ~3.5 minutes total
		baseDelayMs: 1000,                  // Start with 1 second
		maxDelayMs: 15000,                  // Cap at 15 seconds
		exponentialBase: 1.5,               // Gentler growth than 2.0
		currentAttempt: 0
	};

	// Provider health tracker
	private providerHealthTracker: ProviderHealthState = {
		state: 'HEALTHY',
		failureCount: 0,
		lastFailureTime: 0,
		nextAttemptTime: 0
	};

	private readonly HEALTH_FAILURE_THRESHOLD: number = 5;      // Mark degraded after 5 failures
	private readonly HEALTH_RECOVERY_TIMEOUT: number = 60000;   // Stay degraded for 60 seconds

	// Token refresh state tracking (prevents concurrent refresh attempts)
	private isRefreshingToken: boolean = false;
	private refreshTokenPromise: Promise<void> | null = null;

	// Storage change debouncing (multi-tab coordination)
	private storageChangeDebounceTimer: number | null = null;
	private readonly STORAGE_CHANGE_DEBOUNCE_MS: number = 500;

	// Session check throttling
	private lastSessionCheck: number = 0;
	private readonly SESSION_CHECK_THROTTLE_MS: number = 30000;
	private readonly lastSessionCheckPerTab: Map<string, number> = new Map();

	// Automatic token refresh timer (replaces library's broken automatic refresh)
	private tokenRefreshTimer?: number;
	private isHandlingTokenExpires: boolean = false;

	// Configuration
	private authCodeFlowConfig?: AuthConfig;
	private authConfigurationCache: IAuthProviderConfiguration | undefined;

	// Storage event listener reference for cleanup
	private storageEventListener?: (event: StorageEvent) => void;

	constructor(private readonly oauthService: OAuthService, readonly router: Router) {
		super();
		this.initializeService();
	}

	// ============================================================================
	// INITIALIZATION & SETUP
	// ============================================================================

	/**
	 * Initialize all service components
	 */
	private initializeService(): void {
		this.monitorTokenChanges();
		this.monitorNetworkStatus();
		this.setupDebugTools();
		this.setupCleanup();
	}

	/**
	 * Setup cleanup on destroy to prevent memory leaks
	 */
	private setupCleanup(): void {
		this.destroyRef.onDestroy(() => {
			// Cleanup storage listener
			if (this.storageEventListener && this.isBrowser()) {
				this.getWindow().removeEventListener('storage', this.storageEventListener);
			}

			// Cleanup timers
			if (this.storageChangeDebounceTimer) {
				clearTimeout(this.storageChangeDebounceTimer);
			}

			if (this.tokenRefreshTimer) {
				clearTimeout(this.tokenRefreshTimer);
			}

			console.log('[AuthService] Cleanup completed');
		});
	}

	/**
	 * Setup debug tools for development
	 */
	private setupDebugTools(): void {
		if (!this.isBrowser()) {
			return;
		}

		if (!this.isProduction()) {
			(this.getWindow() as any).debugAuth = this;
			console.log(
				`%c[AuthService] 🔧 Debug Tools Available: window.debugAuth.debugTokenState()`,
				`color: ${this.LOG_COLORS.DEBUG};`
			);
		}
	}

	// ============================================================================
	// TOKEN REFRESH
	// ============================================================================

	/**
	 * Setup automatic token refresh that works under high load
	 * Replaces angular-oauth2-oidc's broken setupAutomaticSilentRefresh()
	 *
	 * Reason:
	 * - Library's automatic refresh uses setTimeout which gets blocked under load
	 *
	 * Approach:
	 * - Use queueMicrotask() for high-priority execution
	 * - Add verification safety net
	 * - Implement exponential backoff retry
	 * - Consider provider health
	 */
	private setupAutomaticTokenRefresh(): void {
		console.log('[AuthService] Automatic token refresh setup');

		this.oauthService.events
			.pipe(
				filter((e: OAuthEvent) =>
					e.type === 'token_received' || e.type === 'token_refreshed'
				),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((event: OAuthEvent) => {
				console.log(`[AuthService] Token event ${event.type} - starting refresh cycle`);
				this.scheduleAutomaticTokenRefresh();
			});
	}

	/**
	 * Schedule the next token refresh with verification safety net
	 *
	 * Execution flow:
	 * 1. Calculate refresh time (75% of token lifetime)
	 * 2. Schedule refresh using setTimeout + queueMicrotask
	 * 3. Schedule verification check
	 * 4. If verification fails, force refresh
	 */
	private scheduleAutomaticTokenRefresh(): void {
		const claims = this.getOAuthIdentityClaims();
		if (!claims?.exp || !claims.iat) {
			console.warn('[AuthService] Cannot schedule refresh - no token claims');
			return;
		}

		const now = Date.now() / 1000;
		const exp = claims.exp;
		const iat = claims.iat;
		const lifetime = exp - iat;
		const timeoutFactor = this.authCodeFlowConfig?.timeoutFactor || 0.75;

		const refreshAt = iat + (lifetime * timeoutFactor);
		const delayMs = Math.max(0, (refreshAt - now) * 1000);

		// Clear existing timer (prevents duplicate schedules)
		if (this.tokenRefreshTimer) {
			clearTimeout(this.tokenRefreshTimer);
			console.log('[AuthService] Cleared previous refresh timer');
		}

		console.log(
			`[AuthService] Scheduling automatic token refresh in ${Math.round(delayMs / 1000)}s ` +
			`(at ${new Date(refreshAt * 1000).toISOString()})`
		);

		if (!this.isBrowser()) {
			return;
		}

		// Schedule the refresh with high-priority execution
		this.tokenRefreshTimer = this.getWindow().setTimeout(() => {
			console.log(`[AuthService] Scheduled refresh executed`);
			queueMicrotask(async () => {
				await this.executeScheduledRefresh();
			});
		}, delayMs);

		// Add verification check (safety net)
		this.scheduleRefreshVerification(claims, delayMs);
	}

	/**
	 * Execute scheduled token refresh
	 * Separated for better error handling and readability
	 */
	private async executeScheduledRefresh(): Promise<void> {
		// Clear the timer reference immediately
		this.tokenRefreshTimer = undefined;

		const canRefresh = this.oauthService.hasValidAccessToken() &&
			this.isOnlineSubject$.getValue() &&
			this.isProviderHealthy();

		if (!canRefresh) {
			console.warn(
				`%c[AuthService] Cannot execute scheduled refresh - conditions not met`,
				`color: ${this.LOG_COLORS.WARNING};`
			);
			console.table({
				'Has valid token': this.oauthService.hasValidAccessToken(),
				'Is online': this.isOnlineSubject$.getValue(),
				'Provider healthy': this.isProviderHealthy()
			});
			console.log('[AuthService] Retrying in 10s...');
			setTimeout(() => this.scheduleAutomaticTokenRefresh(), 10000);
			return;
		}

		console.log(`[AuthService] Executing scheduled token refresh...`);

		try {
			await this.refreshTokenWithTracking();
			console.log(`%c[AuthService] Scheduled refresh successful`, `color: ${this.LOG_COLORS.SUCCESS};`);
			// Next refresh will be scheduled by token_refreshed event
			// If event doesn't fire within 2s, verification will catch it

		} catch (error) {
			console.error(`%c[AuthService] Scheduled refresh failed`, `color: ${this.LOG_COLORS.ERROR};`, error);

			// Retry with exponential backoff
			const retryDelay = Math.min(60000, 10000 * Math.pow(2, this.retryConfig.currentAttempt));
			console.warn(`[AuthService] Retrying refresh in ${retryDelay / 1000}s`);

			setTimeout(() => this.scheduleAutomaticTokenRefresh(), retryDelay);
		}
	}

	/**
	 * Schedule verification to ensure refresh actually happened
	 * This is a safety net in case the main refresh timer fails
	 *
	 * Reason:
	 * - Under extreme load, even queueMicrotask can be delayed
	 * - Browser tab might be backgrounded during refresh window
	 * - Provider might be slow to respond
	 *
	 * Flow:
	 * - Wait 15-45 seconds after expected refresh time
	 * - Check if token was actually refreshed (compare iat)
	 * - If not refreshed, force refresh
	 */
	private scheduleRefreshVerification(originalClaims: IOpenIDClaims, expectedRefreshDelayMs: number): void {
		const claims = this.getOAuthIdentityClaims();
		const tokenLifetime = (claims?.exp || 0) - (claims?.iat || 0);

		// Use 10% of token lifetime, capped between 15-45 seconds
		const verificationDelayOffset = Math.max(15000, Math.min(45000, tokenLifetime * 1000 * 0.1));
		const verificationDelay = expectedRefreshDelayMs + verificationDelayOffset;

		console.log(
			`[AuthService] Scheduling verification check in ${Math.round(verificationDelayOffset / 1000)}s ` +
			`(at ${new Date(Date.now() + verificationDelay).toISOString()})`
		);

		setTimeout(() => {
			const currentClaims = this.getOAuthIdentityClaims();
			const wasRefreshed = currentClaims?.iat !== originalClaims.iat;

			if (wasRefreshed) {
				console.log(`%c[AuthService] Refresh verification passed`, `color: ${this.LOG_COLORS.SUCCESS};`);

				if (!this.tokenRefreshTimer) {
					console.warn(
						`%c[AuthService] Verification passed but no timer scheduled - rescheduling now`,
						`color: ${this.LOG_COLORS.WARNING};`
					);
					this.scheduleAutomaticTokenRefresh();
				}
			} else {
				console.error(
					`%c[AuthService] Verification failed: Token was not refreshed! Retrying refresh...`,
					`color: ${this.LOG_COLORS.ERROR};`
				);

				queueMicrotask(async () => {
					try {
						await this.refreshTokenWithTracking();
						console.log(`%c[AuthService] Token refresh successful`, `color: ${this.LOG_COLORS.SUCCESS};`);
						this.scheduleAutomaticTokenRefresh();
					} catch (error) {
						console.error(`%c[AuthService] Token refresh failed`, `color: ${this.LOG_COLORS.ERROR};`, error);
						setTimeout(() => this.attemptTokenRefreshWithRetry(5), 5000);
					}
				});
			}
		}, verificationDelay);
	}
	/**
	 * Wrapper for refresh token with state tracking
	 * Prevents concurrent refresh attempts
	 */
	private async refreshTokenWithTracking(): Promise<void> {
		if (this.isRefreshingToken && this.refreshTokenPromise) {
			console.log('[AuthService] Refresh already in progress, reusing existing promise');
			return this.refreshTokenPromise;
		}

		this.isRefreshingToken = true;
		this.refreshTokenPromise = this.oauthService.refreshToken()
			.then(() => {
				this.isRefreshingToken = false;
				this.refreshTokenPromise = null;
			})
			.catch((error) => {
				this.isRefreshingToken = false;
				this.refreshTokenPromise = null;
				throw error;
			});

		return this.refreshTokenPromise;
	}

	// ============================================================================
	// OAUTH EVENT MONITORING
	// ============================================================================

	/**
	 * Monitor OAuth token lifecycle events with enhanced error handling
	 */
	private monitorTokenChanges(): void {
		this.oauthService.events
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(event => {
				this.logOAuthEvent(event);

				if (event instanceof OAuthErrorEvent) {
					this.handleOAuthError(event);
				} else if (event instanceof OAuthSuccessEvent) {
					this.handleOAuthSuccess(event);
				} else {
					this.handleOAuthInfoEvent(event);
				}
			});

		// Handle critical OAuth errors
		merge(
			this.oauthService.events.pipe(
				filter((e: OAuthEvent) => e.type === 'invalid_nonce_in_state')
			),
			this.oauthService.events.pipe(
				filter((e: OAuthEvent) => ['session_terminated', 'session_error'].includes(e.type))
			)
		)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(async (event: OAuthEvent) => {
				console.error('[AuthService] Critical OAuth error:', event.type);
				await this.handleCriticalOAuthError();
			});

		this.setupStorageListener();
		this.setupVisibilityListener();
	}

	/**
	 * Enhanced logging for OAuth events
	 */
	private logOAuthEvent(event: OAuthEvent): void {
		const timestamp = new Date().toISOString();
		const eventType = event.type;

		if (event instanceof OAuthErrorEvent) {
			console.error(`[AuthService] ${timestamp} - OAuthErrorEvent:`, {
				type: eventType,
				reason: event.reason,
				params: event.params
			});
		} else if (event instanceof OAuthSuccessEvent) {
			console.info(`[AuthService] ${timestamp} - OAuthSuccessEvent:`, eventType);
		} else {
			console.log(`[AuthService] ${timestamp} - OAuthInfoEvent:`, eventType);
		}
	}

	/**
	 * Handle OAuth info events (token_expires, etc.)
	 */
	private handleOAuthInfoEvent(event: OAuthEvent): void {
		switch (event.type) {
			case 'token_expires':
				this.handleTokenExpires();
				break;

			case 'silently_refreshed':
				// This shouldn't fire since we don't use library's automatic refresh
				// But if it does (manual refresh call), handle it
				console.log('[AuthService] Token silently refreshed');
				this.resetRetryConfig(this.retryConfig);
				this.resetProviderHealth();
				break;

			case 'token_validation_error':
				console.error('[AuthService] Token validation error detected');
				this.handleInvalidToken();
				break;

			default:
				break;
		}
	}

	/**
	 * Enhanced OAuth error handling with retry logic
	 */
	private async handleOAuthError(event: OAuthErrorEvent): Promise<void> {
		switch (event.type) {
			case 'token_refresh_error':
				await this.handleTokenRefreshError(event);
				break;

			case 'discovery_document_load_error':
				console.error('[AuthService] Failed to load discovery document');
				this.oauthConfigErrorSubject$.next(true);
				this.isDoneLoadingSubject$.next(true);
				break;

			case 'invalid_nonce_in_state':
				console.error('[AuthService] Invalid nonce - possible CSRF attack');
				await this.handleCriticalOAuthError();
				break;

			case 'token_validation_error':
				console.error('[AuthService] Token validation failed');
				await this.handleInvalidToken();
				break;

			default:
				console.error('[AuthService] Unhandled OAuth error:', event.type);
				this.oauthConfigErrorSubject$.next(true);
				break;
		}
	}

	/**
	 * Handle token refresh errors with immediate connection error detection
	 */
	private async handleTokenRefreshError(event: OAuthErrorEvent): Promise<void> {
		const config = this.retryConfig;

		console.error(
			`[AuthService] Token refresh failed (attempt ${config.currentAttempt + 1}/${this.getEffectiveMaxAttempts(event, config)})`,
			{
				reason: event.reason,
				params: event.params
			}
		);

		// Check if refresh token is still available
		const refreshToken = this.oauthService.getRefreshToken();
		if (!refreshToken) {
			console.error('[AuthService] No refresh token available - cannot retry');
			this.resetRetryConfig(config);
			await this.handleInvalidToken();
			return;
		}

		// Detect connection errors immediately and mark provider as degraded
		if (this.isConnectionErrorFromOAuthEvent(event)) {
			console.warn('[AuthService] Connection error detected - marking provider as degraded');
			this.markProviderDegraded(true);
		}

		// Check if this is an auth error (token revoked/invalid)
		if (this.isAuthErrorFromOAuthEvent(event)) {
			console.error('[AuthService] Auth error detected - token likely revoked');
			if (config.currentAttempt >= config.maxAttemptsForAuthErrors) {
				console.error('[AuthService] Max auth error retries reached - logging out');
				await this.handleInvalidToken();
				return;
			}
		}

		// Get effective max attempts based on error type
		const effectiveMaxAttempts = this.getEffectiveMaxAttempts(event, config);

		// Check if retry is necessary
		if (config.currentAttempt < effectiveMaxAttempts) {
			await this.performTokenRefreshRetry(config);
		} else {
			// All retries exhausted
			console.error(`[AuthService] All retries exhausted (${effectiveMaxAttempts} attempts)`);
			this.resetRetryConfig(config);
			await this.handleCriticalOAuthError();
		}
	}

	/**
	 * Handle OAuth success events
	 */
	private handleOAuthSuccess(event: OAuthSuccessEvent): void {
		if (event.type === 'token_received' || event.type === 'token_refreshed') {
			this.handleTokenReceivedOrRefreshed(event);
		} else if (event.type === 'user_profile_loaded') {
			console.log('[AuthService] User profile loaded');
			this.updateAuthenticationState(this.oauthService.hasValidAccessToken());
			this.updateOAuthConfigErrorState(false);
		}
	}

	private handleTokenReceivedOrRefreshed(event: OAuthSuccessEvent): void {
		const hasValidToken = this.oauthService.hasValidAccessToken();
		const wasAuthenticated = this.isAuthenticatedSubject$.getValue();
		const refreshToken = this.oauthService.getRefreshToken();

		console.log('[AuthService] Token received/refreshed', {
			eventType: event.type,
			hasAccessToken: hasValidToken,
			hasRefreshToken: !!refreshToken,
			wasAuthenticated
		});

		if (!refreshToken && event.type === 'token_received') {
			console.error(`%c[AuthService] No refresh token! Check Auth Provider scope configuration (needs offline_access)`, `color: ${this.LOG_COLORS.CRITICAL};`);
		}

		if (event.type === 'token_refreshed') {
			this.resetRetryConfig(this.retryConfig);
			this.resetProviderHealth();
		}

		this.updateAuthenticationState(hasValidToken);
		this.updateOAuthConfigErrorState(false);
		this.updateDoneLoadingState(true);

		if (event.type === 'token_received' && !wasAuthenticated && !this.isOnCallbackPage()) {
			this.handleTokenReceived();
		}
	}

	// ============================================================================
	// ERROR CLASSIFICATION & RETRY LOGIC
	// ============================================================================

	/**
	 * Determine effective max attempts based on error type
	 */
	private getEffectiveMaxAttempts(error: any, config: RetryConfig): number {
		// Check if it's an OAuth error event
		if (error && 'reason' in error) {
			if (this.isAuthErrorFromOAuthEvent(error)) {
				return config.maxAttemptsForAuthErrors;
			}
			if (this.isConnectionErrorFromOAuthEvent(error)) {
				return config.maxAttemptsForConnectionErrors;
			}
		}

		// Check if it's an HttpErrorResponse
		if (error instanceof HttpErrorResponse) {
			if (error.status === 401 || error.status === 403) {
				return config.maxAttemptsForAuthErrors;
			}
			if (this.isConnectionError(error)) {
				return config.maxAttemptsForConnectionErrors;
			}
		}

		// Default to connection error attempts (conservative approach)
		console.warn('[AuthService] Unclassified error type, using connection error retry limit');
		return config.maxAttemptsForConnectionErrors;
	}

	/**
	 * Check if OAuthErrorEvent represents an auth error (401/403)
	 */
	private isAuthErrorFromOAuthEvent(event: OAuthErrorEvent): boolean {
		const reasonStr = JSON.stringify(event.reason || {});
		const paramsStr = JSON.stringify(event.params || {});

		return reasonStr.includes('401') ||
			reasonStr.includes('403') ||
			reasonStr.includes('Unauthorized') ||
			reasonStr.includes('Forbidden') ||
			paramsStr.includes('"status":401') ||
			paramsStr.includes('"status":403');
	}

	/**
	 * Check if OAuthErrorEvent represents a connection error
	 */
	private isConnectionErrorFromOAuthEvent(event: OAuthErrorEvent): boolean {
		const reasonStr = JSON.stringify(event.reason || {});
		const paramsStr = JSON.stringify(event.params || {});

		return reasonStr.includes('timeout') ||
			reasonStr.includes('Failed to fetch') ||
			reasonStr.includes('Network') ||
			reasonStr.includes('ERR_') ||
			paramsStr.includes('"status":0') ||
			paramsStr.includes('"status":502') ||
			paramsStr.includes('"status":503') ||
			paramsStr.includes('"status":504');
	}

	/**
	 * Helper: Check if error is a connection error
	 */
	private isConnectionError(error: any): boolean {
		if (error instanceof HttpErrorResponse) {
			return error.status === 0 || error.status === 502 || error.status === 503 || error.status === 504;
		}

		return error?.message?.includes('timeout') ||
			error?.message?.includes('Failed to fetch') ||
			error?.message?.includes('Network') ||
			error?.message?.includes('ERR_');
	}

	/**
	 * Perform token refresh retry with provider health awareness
	 */
	private async performTokenRefreshRetry(config: RetryConfig): Promise<void> {
		// Check provider health state
		if (this.isProviderDegraded()) {
			const now = Date.now();
			if (now < this.providerHealthTracker.nextAttemptTime) {
				const waitTimeSeconds = Math.round((this.providerHealthTracker.nextAttemptTime - now) / 1000);
				console.warn(
					`[AuthService] Provider DEGRADED - skipping retry (next attempt in ${waitTimeSeconds}s)`
				);
				return;
			} else {
				console.log('[AuthService] Provider health check timeout elapsed - entering recovering state');
				this.providerHealthTracker.state = 'RECOVERING';
			}
		}

		const delayMs = this.calculateBackoffDelay(config, config.currentAttempt);
		config.currentAttempt++;

		console.log(`[AuthService] Retrying token refresh in ${delayMs}ms...`);

		await this.delay(delayMs);

		if (!this.isOnlineSubject$.getValue()) {
			console.warn('[AuthService] Cannot retry - offline');
			return;
		}

		try {
			await this.refreshTokenWithTracking();
			console.log(`%c[AuthService] Token refresh retry succeeded`, `color: ${this.LOG_COLORS.SUCCESS};`);
			this.resetRetryConfig(config);
			this.resetProviderHealth();
		} catch (error) {
			console.error('[AuthService] Token refresh retry failed', error);
			if (this.isConnectionError(error)) {
				this.markProviderDegraded(true);
			}
		}
	}

	/**
	 * Calculate exponential backoff delay with jitter
	 * Prevents thundering herd problem
	 */
	private calculateBackoffDelay(config: RetryConfig, attempt: number): number {
		const { baseDelayMs, maxDelayMs, exponentialBase } = config;

		const exponentialDelay = baseDelayMs * Math.pow(exponentialBase, attempt);
		const cappedDelay = Math.min(exponentialDelay, maxDelayMs);

		// Add 10% random jitter to prevent synchronized retries
		const jitter = Math.random() * cappedDelay * 0.1;
		return Math.floor(cappedDelay + jitter);
	}

	/**
	 * Reset retry configuration
	 */
	private resetRetryConfig(config: RetryConfig): void {
		config.currentAttempt = 0;
	}

	// ============================================================================
	// PROVIDER HEALTH TRACKING
	// ============================================================================

	/**
	 * Check if provider is healthy
	 */
	private isProviderHealthy(): boolean {
		return this.providerHealthTracker.state === 'HEALTHY';
	}

	/**
	 * Check if provider is degraded
	 */
	private isProviderDegraded(): boolean {
		return this.providerHealthTracker.state === 'DEGRADED';
	}

	/**
	 * Reset provider health on successful operation
	 */
	private resetProviderHealth(): void {
		if (this.providerHealthTracker.failureCount > 0 ||
			this.providerHealthTracker.state !== 'HEALTHY') {
			console.log('[AuthService] Provider health reset to healthy');
		}

		this.providerHealthTracker = {
			state: 'HEALTHY',
			failureCount: 0,
			lastFailureTime: 0,
			nextAttemptTime: 0
		};
	}

	/**
	 * Mark provider as degraded
	 */
	private markProviderDegraded(isConnectionError: boolean): void {
		if (!isConnectionError) {
			return;
		}

		this.providerHealthTracker.failureCount++;
		this.providerHealthTracker.lastFailureTime = Date.now();

		if (this.providerHealthTracker.failureCount >= this.HEALTH_FAILURE_THRESHOLD) {
			this.providerHealthTracker.state = 'DEGRADED';
			this.providerHealthTracker.nextAttemptTime = Date.now() + this.HEALTH_RECOVERY_TIMEOUT;

			console.warn(
				`%c[AuthService] Provider marked as degraded after ${this.providerHealthTracker.failureCount} failures ` +
				`(- will retry in ${this.HEALTH_RECOVERY_TIMEOUT / 1000}s)`,
				`color: ${this.LOG_COLORS.CRITICAL};`
			);
		}
	}

	// ============================================================================
	// CRITICAL ERROR HANDLING
	// ============================================================================

	/**
	 * Handle critical OAuth errors that require immediate logout
	 */
	async handleCriticalOAuthError(): Promise<void> {
		console.error('[AuthService] Handling critical OAuth error');

		const hasToken = this.oauthService.hasValidAccessToken();
		const hasRefreshToken = this.oauthService.getRefreshToken();
		const isOnline = this.isOnlineSubject$.getValue();

		const shouldAttemptServerLogout = this.isProviderHealthy() && isOnline && (hasToken || hasRefreshToken);

		console.log('[AuthService] Provider healthy:', this.isProviderHealthy());
		console.log('[AuthService] Should attempt server logout:', shouldAttemptServerLogout);

		if (shouldAttemptServerLogout) {
			try {
				console.log('[AuthService] Attempting server-side logout...');
				await this.oauthService.revokeTokenAndLogout();
				console.log('[AuthService] Server-side logout successful');
				return;
			} catch (error) {
				console.warn('[AuthService] Server-side logout failed:', error);

				if (this.isConnectionError(error)) {
					console.warn('[AuthService] Marking provider as degraded');
					this.markProviderDegraded(true);
				}
			}
		} else {
			console.log('[AuthService] Skipping server logout (provider degraded or offline)');
		}

		// Always perform local cleanup
		console.log('[AuthService] Performing local cleanup...');
		this.clearAuthState();
		this.clearProviderCookies();

		this.isAuthenticatedSubject$.next(false);
		this.isDoneLoadingSubject$.next(true);
		this.oauthConfigErrorSubject$.next(true);

		// Check if we're already on callback page
		const isOnCallbackPage = this.isOnCallbackPage();

		if (isOnCallbackPage) {
			// Already on callback - let component handle error display
			console.log('[AuthService] Already on callback page, letting component handle error');
			return;
		}

		console.log('[AuthService] Navigating to callback page...');
		try {
			await this.router.navigate(['auth/oidc/callback'], { replaceUrl: true });
		} catch (navError) {
			console.error('[AuthService] Navigation failed:', navError);
		}
	}

	/**
	 * Handle token_expires event as a fallback safety net
	 *
	 * This should rarely fire if the automatic refresh timer is working correctly.
	 * If it does fire, we check if a refresh is scheduled and trigger one if not.
	 */
	private handleTokenExpires(): void {
		console.log('[AuthService] Token expiring soon - checking if refresh is scheduled');

		// Guard against concurrent handling
		if (this.isHandlingTokenExpires) {
			console.log('[AuthService] Already handling token_expires - skipping');
			return;
		}

		// Set guard flag
		this.isHandlingTokenExpires = true;

		// Check if refresh is NOT scheduled
		if (!this.tokenRefreshTimer) {
			console.log(`[AuthService] No refresh scheduled! Attempting refresh now...`);

			queueMicrotask(async () => {
				try {
					await this.attemptTokenRefreshWithRetry(3);
				} finally {
					this.isHandlingTokenExpires = false;
				}
			});
		} else {
			console.log('[AuthService] Refresh already scheduled - token_expires is expected');
			this.isHandlingTokenExpires = false;
		}
	}

	/**
	 * Handle invalid token scenarios
	 */
	private async handleInvalidToken(): Promise<void> {
		console.log('[AuthService] Handling invalid token');

		const isOnCallbackPage = this.isOnCallbackPage();
		const providerDegraded = this.isProviderDegraded();

		// If provider is degraded and we're on callback page, stay there and show error
		if (isOnCallbackPage && providerDegraded) {
			console.log('[AuthService] On callback page with degraded provider - signaling error');
			this.clearAuthState();
			this.clearProviderCookies();
			this.isAuthenticatedSubject$.next(false);
			this.isDoneLoadingSubject$.next(true);
			this.oauthConfigErrorSubject$.next(true);
			// Don't navigate - let callback component handle it
			return;
		}

		// Otherwise, proceed with critical error handler
		await this.handleCriticalOAuthError();
	}

	/**
	 * Clear authentication state
	 */
	private clearAuthState(): void {
		this.authCodeFlowConfig = undefined as any;
		sessionStorage.removeItem('local_provider');
		sessionStorage.removeItem('extern_provider');
		this.resetRetryConfig(this.retryConfig);
		this.resetProviderHealth();
		this.isRefreshingToken = false;
		this.refreshTokenPromise = null;

		if (this.tokenRefreshTimer) {
			clearTimeout(this.tokenRefreshTimer);
			this.tokenRefreshTimer = undefined;
		}
	}

	// ============================================================================
	// MULTI-TAB SYNCHRONIZATION
	// ============================================================================

	/**
	 * Enhanced storage event listener with cleanup
	 */
	private setupStorageListener(): void {
		if (!this.isBrowser()) {
			return;
		}

		this.storageEventListener = (event: StorageEvent) => {
			this.handleStorageEvent(event);
		};

		this.getWindow().addEventListener('storage', this.storageEventListener);
	}

	private handleStorageEvent(event: StorageEvent): void {
		console.log('[AuthService] Storage event detected', {
			key: event.key,
			oldValue: event.oldValue ? 'existed' : 'null',
			newValue: event.newValue ? 'exists' : 'null',
			url: event.url,
			timestamp: new Date().toISOString()
		});

		if (this.storageChangeDebounceTimer) {
			clearTimeout(this.storageChangeDebounceTimer);
		}

		if (!this.isBrowser()) {
			return;
		}

		if (event.key === 'access_token' && event.newValue === null && event.oldValue !== null) {
			console.log('[AuthService] Token removed in another tab - verifying with debounce...');

			this.storageChangeDebounceTimer = this.getWindow().setTimeout(() => {
				this.handleTokenRemovedFromStorage();
			}, this.STORAGE_CHANGE_DEBOUNCE_MS);
		}
		else if (event.key === 'access_token' && event.newValue !== null) {
			console.log('[AuthService] Token updated in another tab');
			this.updateAuthenticationState(this.oauthService.hasValidAccessToken());
		}
		else if (event.key === 'refresh_token' && event.newValue === null && event.oldValue !== null) {
			console.warn('[AuthService] Refresh token removed in another tab');

			this.storageChangeDebounceTimer = this.getWindow().setTimeout(() => {
				this.handleRefreshTokenRemovedFromStorage();
			}, this.STORAGE_CHANGE_DEBOUNCE_MS);
		}
	}

	/**
	 * Handle token removal from storage with recovery attempts
	 */
	private async handleTokenRemovedFromStorage(): Promise<void> {
		if (this.oauthService.hasValidAccessToken()) {
			console.log('[AuthService] Token restored - refresh completed successfully');
			this.updateAuthenticationState(true);
			return;
		}

		if (this.isRefreshingToken) {
			console.log('[AuthService] Token refresh in progress - waiting for completion...');
			await this.waitForTokenRefresh(3000);

			if (this.oauthService.hasValidAccessToken()) {
				console.log('[AuthService] Token available after refresh wait');
				this.updateAuthenticationState(true);
				return;
			}
		}

		const refreshToken = this.oauthService.getRefreshToken();
		if (refreshToken) {
			console.log('[AuthService] Access token missing but refresh token exists - attempting recovery');

			try {
				await this.attemptTokenRefreshWithRetry(2);
				console.log('[AuthService] Token recovery successful');
				this.updateAuthenticationState(true);
			} catch (error) {
				console.error('[AuthService] Token recovery failed', error);
				await this.handleInvalidToken();
			}
		} else {
			console.warn('[AuthService] Confirmed: Token removed by another tab (no refresh token)');
			await this.handleInvalidToken();
		}
	}

	/**
	 * Handle refresh token removal from storage
	 */
	private async handleRefreshTokenRemovedFromStorage(): Promise<void> {
		if (!this.oauthService.hasValidAccessToken() && !this.oauthService.getRefreshToken()) {
			console.warn('[AuthService] Both tokens removed - user logged out in another tab');
			await this.handleInvalidToken();
		}
	}

	// ============================================================================
	// SESSION VALIDATION & VISIBILITY HANDLING
	// ============================================================================

	/**
	 * Enhanced visibility change listener with per-tab throttling
	 */
	private setupVisibilityListener(): void {
		if (typeof document === 'undefined') return;

		document.addEventListener('visibilitychange', async () => {
			await this.handleVisibilityChange();
		});
	}

	private async handleVisibilityChange(): Promise<void> {
		if (document.hidden || !this.isAuthenticatedSubject$.getValue()) {
			return;
		}

		const now = Date.now();
		const tabId = this.getTabId();

		// Check global throttle
		if (now - this.lastSessionCheck < this.SESSION_CHECK_THROTTLE_MS) {
			console.log('[AuthService] Session check throttled (global)');
			return;
		}

		// Check per-tab throttle
		const lastCheckForTab = this.lastSessionCheckPerTab.get(tabId) || 0;
		if (now - lastCheckForTab < this.SESSION_CHECK_THROTTLE_MS) {
			console.log('[AuthService] Session check throttled (per-tab)');
			return;
		}

		console.log('[AuthService] Tab became visible, checking session validity');
		this.lastSessionCheck = now;
		this.lastSessionCheckPerTab.set(tabId, now);

		await this.delay(200);
		await this.checkSessionValidity();
	}

	/**
	 * Get unique tab identifier
	 */
	private getTabId(): string {
		if (!this.isBrowser()) return 'server';

		let tabId = sessionStorage.getItem('tab_id');
		if (!tabId) {
			tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
			sessionStorage.setItem('tab_id', tabId);
		}
		return tabId;
	}

	/**
	 * Check if token should be refreshed
	 */
	private shouldRefreshToken(): boolean {
		if (!this.oauthService.hasValidAccessToken()) {
			return true;
		}

		const claims = this.getOAuthIdentityClaims();
		if (!claims?.exp || !claims.iat) {
			return false;
		}

		const now = Date.now() / 1000;
		const exp = claims.exp;
		const iat = claims.iat;
		const tokenLifetime = exp - iat;
		const timeToExpiry = exp - now;
		const timeoutFactor = this.authCodeFlowConfig?.timeoutFactor || 0.75;

		const refreshScheduledIn = timeToExpiry - (tokenLifetime * (1 - timeoutFactor));

		const isOverdue = refreshScheduledIn < -10;
		const isExpiringSoon = timeToExpiry < 120;

		if (isOverdue || isExpiringSoon) {
			console.warn('[AuthService] Token needs refresh:', {
				'Refresh Scheduled In (sec)': Math.round(refreshScheduledIn),
				'Time to Expiry (sec)': Math.round(timeToExpiry),
				'Is Overdue': isOverdue,
				'Is Expiring Soon': isExpiringSoon
			});
			return true;
		}

		return false;
	}

	/**
	 * Check session validity with proper expired token handling during outage
	 */
	private async checkSessionValidity(): Promise<void> {
		const wasAuthenticated = this.isAuthenticatedSubject$.getValue();

		console.log(`%c[AuthService] Checking session validity...`, `color: ${this.LOG_COLORS.WARNING};`);
		console.table({
			'Authenticated': wasAuthenticated,
			'Has Access Token': !!this.oauthService.getAccessToken(),
			'Has Refresh Token': !!this.oauthService.getRefreshToken(),
			'Is Online': this.isOnlineSubject$.getValue(),
			'Is Refreshing': this.isRefreshingToken,
			'Provider Health': this.providerHealthTracker.state,
			'Timestamp': new Date().toISOString()
		});

		if (!this.isOnlineSubject$.getValue()) {
			console.warn('[AuthService] Cannot validate session - offline');
			return;
		}

		// Check token expiration before attempting refresh
		// This prevents stuck state where token expires during provider outage
		const hasValidToken = this.oauthService.hasValidAccessToken();
		const providerDegraded = this.isProviderDegraded();

		if (!hasValidToken && providerDegraded) {
			const now = Date.now();
			const canRetry = now >= this.providerHealthTracker.nextAttemptTime;

			if (!canRetry) {
				const waitSeconds = Math.round((this.providerHealthTracker.nextAttemptTime - now) / 1000);
				console.error(
					`%c[AuthService] Token expired but provider degraded (retry in ${waitSeconds}s) - logging out to prevent stuck state`,
					`color: ${this.LOG_COLORS.ERROR};`
				);
				await this.handleInvalidToken();
				return;
			} else {
				// Provider health check timeout elapsed, transition to RECOVERING
				console.log('[AuthService] Provider health check timeout elapsed, entering recovering state');
				this.providerHealthTracker.state = 'RECOVERING';
			}
		}

		if (this.shouldRefreshToken()) {
			console.warn('[AuthService] Token refresh is needed or expiring soon - triggering refresh');
			await this.attemptTokenRefreshWithRetry();
			return;
		}

		if (this.isRefreshingToken) {
			console.log('[AuthService] Token refresh in progress, waiting...');
			const refreshSucceeded = await this.waitForTokenRefresh(5000);

			if (!refreshSucceeded) {
				console.warn('[AuthService] Token refresh failed or timed out');
			}
		}

		if (!this.oauthService.hasValidAccessToken()) {
			console.warn('[AuthService] Session check: Token expired or invalid');

			if (wasAuthenticated) {
				await this.attemptTokenRefreshWithRetry();
			} else {
				console.log('[AuthService] Already logged out.');
			}
			return;
		}

		await this.validateWithServerAndHandle();
	}

	private async validateWithServerAndHandle(): Promise<void> {
		try {
			this.isCheckingConnectionSubject$.next(true);
			const isValid = await this.validateTokenWithServerRetry();

			if (isValid) {
				console.log(`%c[AuthService] Session check: Token valid`, `color: ${this.LOG_COLORS.SUCCESS};`);
			} else {
				console.warn('[AuthService] Session check: Token invalid on server - attempting refresh');
				await this.attemptTokenRefreshWithRetry();
			}
		} catch (error) {
			console.error('[AuthService] Session validation failed after retries');

			if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
				console.error('[AuthService] Auth error - logging out');
				await this.handleInvalidToken();
			} else {
				console.warn('[AuthService] Network issue detected - keeping user logged in (offline-first approach)');
			}
		} finally {
			this.isCheckingConnectionSubject$.next(false);
		}
	}

	/**
	 * Attempt token refresh with provider health awareness and tiered retry limits
	 */
	private async attemptTokenRefreshWithRetry(maxAttempts?: number): Promise<void> {
		// Check provider health state
		if (this.isProviderDegraded()) {
			const now = Date.now();
			if (now < this.providerHealthTracker.nextAttemptTime) {
				const waitTimeSeconds = Math.round((this.providerHealthTracker.nextAttemptTime - now) / 1000);
				console.warn(
					`[AuthService] Provider degraded - skipping refresh attempt (retry in ${waitTimeSeconds}s)`
				);

				// If token is already expired, logout to prevent stuck state
				if (!this.oauthService.hasValidAccessToken()) {
					console.error('[AuthService] Token expired and provider degraded - logging out user');
					await this.handleInvalidToken();
				}
				return;
			} else {
				console.log('[AuthService] Provider health check timeout elapsed - entering recovering state');
				this.providerHealthTracker.state = 'RECOVERING';
			}
		}

		const config = this.retryConfig;
		const attempts = maxAttempts || config.maxAttemptsForConnectionErrors;
		const refreshToken = this.oauthService.getRefreshToken();

		if (!refreshToken) {
			console.error('[AuthService] No refresh token available - cannot refresh');
			await this.handleInvalidToken();
			return;
		}

		for (let attempt = 1; attempt <= attempts; attempt++) {
			const success = await this.tryRefreshAttempt(attempt, attempts, config);
			if (success) {
				return;
			}
		}

		console.error('[AuthService] All token refresh attempts failed');
		await this.handleInvalidToken();
	}

	private async tryRefreshAttempt(attempt: number, maxAttempts: number, config: RetryConfig): Promise<boolean> {
		console.log(`[AuthService] Attempting token refresh (${attempt}/${maxAttempts})...`);

		try {
			await this.refreshTokenWithTracking();
			console.log(`%c[AuthService] Token refresh successful`, `color: ${this.LOG_COLORS.SUCCESS};`);
			this.resetRetryConfig(config);
			this.resetProviderHealth();
			return true;
		} catch (error) {
			console.error(`[AuthService] Token refresh attempt ${attempt} failed:`, error);

			// Check if it's an auth error (400/401) - token revoked or invalid
			if (error instanceof HttpErrorResponse && (error.status === 400 || error.status === 401)) {
				console.error('[AuthService] Refresh token invalid or revoked - cannot retry');
				await this.handleInvalidToken();
				return true; // Stop retry loop
			}

			// Connection error - update provider health
			if (this.isConnectionError(error)) {
				this.markProviderDegraded(true);
			}

			if (attempt < maxAttempts) {
				const delayMs = this.calculateBackoffDelay(config, attempt - 1);
				console.log(`[AuthService] Waiting ${delayMs}ms before retry...`);
				await this.delay(delayMs);

				if (!this.isOnlineSubject$.getValue()) {
					console.warn('[AuthService] Network offline - stopping retries');
					return true; // Stop retry loop
				}
			}

			return false; // Continue retry loop
		}
	}

	/**
	 * Wait for token refresh to complete with event-based detection
	 */
	private waitForTokenRefresh(maxWaitMs: number): Promise<boolean> {
		return new Promise((resolve) => {
			const startTime = Date.now();
			const checkInterval = 200;
			let intervalId: number | undefined;

			const sub = this.oauthService.events.subscribe(event => {
				if (event.type === 'token_refreshed') {
					console.log('[AuthService] Token refresh completed during wait');
					sub.unsubscribe();
					if (intervalId) clearInterval(intervalId);
					resolve(true);
				} else if (event.type === 'token_refresh_error') {
					console.error('[AuthService] Token refresh failed during wait');
					sub.unsubscribe();
					if (intervalId) clearInterval(intervalId);
					resolve(false);
				}
			});

			if (!this.isBrowser()) {
				sub.unsubscribe();
				resolve(false);
				return;
			}

			intervalId = this.getWindow().setInterval(() => {
				const elapsed = Date.now() - startTime;

				if (this.oauthService.hasValidAccessToken() && !this.isRefreshingToken) {
					console.log('[AuthService] Token became valid during wait');
					sub.unsubscribe();
					clearInterval(intervalId);
					resolve(true);
				}
				else if (elapsed >= maxWaitMs) {
					console.warn('[AuthService] Wait for token refresh timed out');
					sub.unsubscribe();
					clearInterval(intervalId);
					resolve(false);
				}
			}, checkInterval);
		});
	}

	/**
	 * Validate token with server using reduced retry attempts
	 */
	private async validateTokenWithServerRetry(attempt: number = 1): Promise<boolean> {
		// Use health-aware max attempts
		const maxAttempts = this.isProviderHealthy() ? 5 : 2;

		try {
			return await this.validateTokenWithServer();
		} catch (error) {
			if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
				console.log(`[AuthService] Token validation: ${error.status} - Token invalid/revoked`);
				return false;
			}

			// Connection error detected
			if (this.isConnectionError(error)) {
				this.markProviderDegraded(true);
			}

			if (attempt <= maxAttempts) {
				console.warn(`[AuthService] Token validation failed (attempt ${attempt}/${maxAttempts}), retrying...`);

				const delayMs = this.calculateBackoffDelay(this.retryConfig, attempt - 1);
				await this.delay(delayMs);

				if (!this.isOnlineSubject$.getValue()) {
					throw new Error('Network offline - stopping retries');
				}

				return this.validateTokenWithServerRetry(attempt + 1);
			}

			console.error('[AuthService] Token validation failed after all retries');
			throw error;
		}
	}

	/**
	 * Validate token with server using Keycloak userinfo endpoint
	 */
	private async validateTokenWithServer(): Promise<boolean> {
		if (!this.oauthService.hasValidAccessToken()) {
			return false;
		}

		const token = this.oauthService.getAccessToken();
		const userInfoEndpoint = this.oauthService.userinfoEndpoint;

		if (!userInfoEndpoint) {
			console.warn('[AuthService] No userinfo endpoint available for validation');
			return true;
		}

		try {
			await firstValueFrom(
				this.http.get(userInfoEndpoint, {
					headers: {
						'Authorization': `Bearer ${token}`
					}
				})
			);

			console.log('[AuthService] Token validated successfully with auth provider');
			return true;
		} catch (error) {
			if (error instanceof HttpErrorResponse) {
				if (error.status === 401 || error.status === 403) {
					console.log('[AuthService] Token validation failed - token was revoked server-side');
					return false;
				}

				console.warn('[AuthService] Token validation encountered error:', {
					status: error.status,
					message: error.message,
					url: error.url
				});
			}

			throw error;
		}
	}

	// ============================================================================
	// NETWORK STATUS MONITORING
	// ============================================================================

	/**
	 * Monitor browser online/offline status with enhanced reconnection handling
	 */
	private monitorNetworkStatus(): void {
		if (!this.isBrowser()) {
			return;
		}

		this.getWindow().addEventListener('online', async () => {
			console.log(`%c[AuthService] Network connection restored`, `color: ${this.LOG_COLORS.SUCCESS};`);
			this.isOnlineSubject$.next(true);

			this.resetRetryConfig(this.retryConfig);

			// Reset provider health when network comes back
			if (this.isProviderDegraded()) {
				console.log('[AuthService] Network restored - resetting provider health');
				this.resetProviderHealth();
			}

			if (this.isAuthenticatedSubject$.getValue()) {
				console.log('[AuthService] Checking session after network reconnection...');
				await this.delay(500);
				await this.handleNetworkReconnection();
			}
		});

		this.getWindow().addEventListener('offline', () => {
			console.warn(`%c[AuthService] Network connection lost`, `color: ${this.LOG_COLORS.WARNING};`);
			this.isOnlineSubject$.next(false);
		});
	}

	/**
	 * Handle network reconnection with token validation
	 */
	private async handleNetworkReconnection(): Promise<void> {
		if (this.oauthService.hasValidAccessToken()) {
			console.log('[AuthService] Token still valid after reconnection');
			await this.checkSessionValidity();
			return;
		}

		console.log('[AuthService] Token invalid after network reconnection - attempting refresh');

		const refreshToken = this.oauthService.getRefreshToken();
		if (refreshToken) {
			try {
				await this.attemptTokenRefreshWithRetry();
				console.log(`%c[AuthService] Token refreshed successfully after reconnection`, `color: ${this.LOG_COLORS.SUCCESS};`);
			} catch (error) {
				console.error('[AuthService] Token refresh failed after reconnection:', error);
				await this.handleInvalidToken();
			}
		} else {
			console.error('[AuthService] No refresh token available - cannot recover from network disconnect');
			await this.handleInvalidToken();
		}
	}

	// ============================================================================
	// UTILITY METHODS
	// ============================================================================

	/**
	 * Check if we're running in a browser environment with window object available
	 */
	private isBrowser(): boolean {
		return typeof globalThis !== 'undefined' && typeof globalThis.window !== 'undefined';
	}

	/**
	 * Get window object safely (only call after isBrowser() check)
	 */
	private getWindow(): Window {
		return globalThis.window;
	}

	private isProduction(): boolean {
		if (!this.isBrowser()) {
			return true;
		}
		return this.getWindow().location.hostname !== 'localhost';
	}

	private isOnCallbackPage(): boolean {
		if (!this.isBrowser()) {
			return false;
		}
		return this.getWindow().location.pathname.includes('/auth/oidc/callback');
	}

	private updateAuthenticationState(isAuthenticated: boolean): void {
		if (this.isAuthenticatedSubject$.getValue() !== isAuthenticated) {
			this.isAuthenticatedSubject$.next(isAuthenticated);
		}
	}

	private updateDoneLoadingState(isDoneLoading: boolean): void {
		if (this.isDoneLoadingSubject$.getValue() !== isDoneLoading) {
			this.isDoneLoadingSubject$.next(isDoneLoading);
		}
	}

	private updateOAuthConfigErrorState(hasError: boolean): void {
		if (this.oauthConfigErrorSubject$.getValue() !== hasError) {
			this.oauthConfigErrorSubject$.next(hasError);
		}
	}

	private async handleTokenReceived(): Promise<void> {
		try {
			console.log('[AuthService] Navigating to auth callback page after token received');
			await this.navigateToAuthCallback();
		} catch (error) {
			console.error('[AuthService] Failed to handle token received:', error);
			this.oauthConfigErrorSubject$.next(true);
		}
	}

	/**
	 * Utility function for delays
	 */
	private delay(ms: number): Promise<void> {
		return new Promise(resolve => setTimeout(resolve, ms));
	}

	// ============================================================================
	// CONFIGURATION & INITIALIZATION
	// ============================================================================

	/**
	 * Sets the configuration for the chosen auth provider
	 */
	public async setAuthConfig(authProvider: IAuthProvider): Promise<void> {
		this.externOAuth2Provider = authProvider;
		this.oauthConfigErrorSubject$.next(false);

		try {
			if (!authProvider.hasOwnProperty('config')) {
				throw new Error('Missing oauth provider configuration');
			}

			if (authProvider.type === providerTypes.oidc) {
				sessionStorage.setItem('extern_provider', authProvider._id);
			}

			if (authProvider.type === providerTypes.local) {
				sessionStorage.setItem('local_provider', authProvider._id);
			}

			const config: IOidcProvider = authProvider.config;

			if (!config.endpoint_url || !config.client_id) {
				throw new Error('Invalid OAuth provider configuration: missing endpoint_url or client_id');
			}

			if (config.redirect_uri === '') {
				config.redirect_uri = `/auth/oidc/callback`;
			}

			if (!this.isBrowser()) {
				throw new Error('Window not available - cannot configure auth in non-browser environment');
			}

			this.authCodeFlowConfig = {
				issuer: config.endpoint_url,
				redirectUri: `${this.getWindow().location.origin}${config.redirect_uri}`,
				postLogoutRedirectUri: `${this.getWindow().location.origin}${config.logout_url}`,
				clientId: config.client_id,
				scope: config.scope.toString().replaceAll(',', ' '),
				responseType: 'code',
				timeoutFactor: 0.75,
				sessionChecksEnabled: false,
				showDebugInformation: false,
				strictDiscoveryDocumentValidation: false,
				requireHttps: this.getWindow().location.protocol === 'https:'
			};

			if (config.client_secret) {
				this.authCodeFlowConfig.dummyClientSecret = config.client_secret;
			}
		} catch (error) {
			console.error('[AuthService] Failed to set auth config:', error);
			this.oauthConfigErrorSubject$.next(true);
			throw error;
		}
	}

	/**
	 * Load the discovery document and configure the OAuth service
	 */
	public async loadDiscoveryDocument(): Promise<void> {
		if (!this.authCodeFlowConfig) {
			const error = new Error('OAuth provider configuration not initialized');
			console.error('[AuthService]', error.message);
			this.oauthConfigErrorSubject$.next(true);
			throw error;
		}

		try {
			console.log('[AuthService] Loading discovery document for:', this.authCodeFlowConfig.issuer);

			this.oauthService.configure(this.authCodeFlowConfig);
			await this.oauthService.loadDiscoveryDocumentAndTryLogin();

			// Setup our automatic token refresh (replaces library's broken automatic refresh)
			// This is called after discovery document is loaded
			this.setupAutomaticTokenRefresh();

			console.log('[AuthService] Discovery document loaded successfully');
			this.isDoneLoadingSubject$.next(true);
			this.oauthConfigErrorSubject$.next(false);
		} catch (error) {
			console.error('[AuthService] Failed to load discovery document:', error);
			this.authCodeFlowConfig = undefined as any;
			this.oauthConfigErrorSubject$.next(true);
			this.isDoneLoadingSubject$.next(true);
			throw error;
		}
	}

	/**
	 * Initialize the authentication service
	 */
	public async initialize(): Promise<void> {
		try {
			const config = await this.loadAuthConfiguration();
			const localProvider = sessionStorage.getItem('local_provider');
			const externProvider = sessionStorage.getItem('extern_provider');

			if (localProvider && config.localProviderEnabled && config?.localOauthProvider) {
				await this.setWithStoredLocalAuthProvider(config, localProvider);
			}
			else if (externProvider && config.oauthProviders.length > 0) {
				await this.setWithStoredExternAuthProvider(config, externProvider);
			} else {
				console.log('[AuthService] No OAuth provider configured');
				this.updateDoneLoadingState(true);
			}
		} catch (error) {
			console.error('[AuthService] Initialization failed:', error);
			this.updateOAuthConfigErrorState(true);
			this.updateDoneLoadingState(true);
		}
	}

	/**
	 * Load authentication configuration from backend
	 */
	private async loadAuthConfiguration(): Promise<IAuthProviderConfiguration> {
		if (this.authConfigurationCache) {
			return this.authConfigurationCache;
		}

		try {
			const providers: IAuthProvider[] = await this.modelService._authProvider.listAll();

			const localProvider = providers.find((item: IAuthProvider) =>
				item.type === 'local'.toUpperCase()
			);

			const oauthProviders = providers.filter((provider: IAuthProvider) =>
				provider.type !== 'local'.toUpperCase() && provider.enabled
			);

			this.authConfigurationCache = {
				localOauthProvider: localProvider,
				localProviderEnabled: localProvider?.enabled || false,
				oauthProviders: oauthProviders
			};

			return this.authConfigurationCache;
		} catch (error) {
			console.error('[AuthService] Failed to load auth configuration:', error);
			this.authConfigurationCache = {
				localProviderEnabled: true,
				oauthProviders: []
			};
			return this.authConfigurationCache;
		}
	}

	/**
	 * Set auth configuration with stored local provider
	 */
	async setWithStoredLocalAuthProvider(config: IAuthProviderConfiguration, localProvider: string): Promise<void> {
		let authProvider = localProvider === config.localOauthProvider?._id ? config.localOauthProvider : undefined;

		if (!authProvider && config?.localOauthProvider) {
			sessionStorage.removeItem('local_provider');
			authProvider = config.localOauthProvider;
		}

		if (authProvider) {
			await this.setAuthConfig(authProvider);
			await this.handleStoredProviderInitialization();
		}
	}

	/**
	 * Set auth configuration with stored external provider
	 */
	async setWithStoredExternAuthProvider(config: IAuthProviderConfiguration, externProvider: string): Promise<void> {
		let authProvider = config.oauthProviders.find(
			(provider: IAuthProvider) => provider._id === externProvider
		);

		if (!authProvider && config.oauthProviders.length === 1) {
			sessionStorage.removeItem('extern_provider');
			authProvider = config.oauthProviders[0];
		}

		if (authProvider) {
			await this.setAuthConfig(authProvider);
			await this.handleStoredProviderInitialization();
		}
	}

	/**
	 * Handle initialization with stored provider
	 */
	private async handleStoredProviderInitialization(): Promise<void> {
		const isCallbackPage = this.isOnCallbackPage();

		if (isCallbackPage) {
			await this.handleCallbackPageInit();
		} else {
			await this.handleNormalPageInit();
		}
	}

	private async handleCallbackPageInit(): Promise<void> {
		console.log('[AuthService] On callback page, loading discovery document');
		await this.loadDiscoveryDocument();

		if (this.oauthService.hasValidAccessToken()) {
			console.log('[AuthService] Valid token found on callback page');
			this.isAuthenticatedSubject$.next(true);
			this.oauthConfigErrorSubject$.next(false);

			try {
				console.log('[AuthService] Loading user details for callback...');
				await this.navigateToAuthCallback();
			} catch (error) {
				console.error('[AuthService] Failed to navigate to callback with user data:', error);
				this.oauthConfigErrorSubject$.next(true);
			}
		}

		this.updateDoneLoadingState(true);
	}

	/**
	 * Handle initialization with login page (not callback)
	 * Call loadDiscoveryDocument() instead of manual setup
	 */
	private async handleNormalPageInit(): Promise<void> {
		if (!this.authCodeFlowConfig) {
			console.log('[AuthService] No AuthProvider Configuration available');
			this.oauthConfigErrorSubject$.next(true);
			this.updateDoneLoadingState(true);
			return;
		}

		// Call loadDiscoveryDocument() which includes setupReliableTokenRefresh()
		// This ensures the automatic refresh mechanism is initialized
		await this.loadDiscoveryDocument();

		if (this.oauthService.hasValidAccessToken()) {
			await this.handleExistingToken();
		} else {
			console.log('[AuthService] No token found in storage');
			this.updateDoneLoadingState(true);
		}
	}

	private async handleExistingToken(): Promise<void> {
		console.log('[AuthService] Token found in storage, validating...');

		try {
			const isValid = await this.validateTokenWithServerRetry();

			if (isValid) {
				console.log('[AuthService] Token validated - auto-authenticating');
				this.isAuthenticatedSubject$.next(true);
				this.oauthConfigErrorSubject$.next(false);
				await this.navigateToAuthCallback();
			} else {
				console.warn('[AuthService] Token validation failed - attempting refresh');
				await this.attemptTokenRefreshWithRetry();

				if (this.oauthService.hasValidAccessToken()) {
					this.isAuthenticatedSubject$.next(true);
					await this.navigateToAuthCallback();
				} else {
					await this.handleInvalidToken();
				}
			}
		} catch (error) {
			console.error('[AuthService] Token validation error:', error);

			if (this.oauthService.getRefreshToken()) {
				try {
					await this.attemptTokenRefreshWithRetry();
					if (this.oauthService.hasValidAccessToken()) {
						this.isAuthenticatedSubject$.next(true);
						await this.navigateToAuthCallback();
					}
				} catch (refreshError) {
					console.error('[AuthService] Recovery failed:', refreshError);
				}
			}
		} finally {
			this.updateDoneLoadingState(true);
		}
	}

	// ============================================================================
	// LOGIN & LOGOUT
	// ============================================================================

	/**
	 * Initiate OAuth login flow
	 */
	public async loginWithOAuth(): Promise<void> {
		if (!this.authCodeFlowConfig) {
			const error = new Error('OAuth provider not configured');
			console.error('[AuthService]', error.message);
			this.oauthConfigErrorSubject$.next(true);
			throw error;
		}

		try {
			if (this.oauthService.hasValidAccessToken()) {
				console.log('[AuthService] Valid token found, navigating to auth callback page');
				await this.navigateToAuthCallback();
				return;
			}

			console.log('[AuthService] Initiating authorization code flow');
			this.oauthService.initCodeFlow();
		} catch (error) {
			console.error('[AuthService] OAuth login failed:', error);
			this.oauthConfigErrorSubject$.next(true);
			throw error;
		}
	}

	/**
	 * Universal logout method
	 */
	public async logout(): Promise<void> {
		try {
			console.log('[AuthService] Logout initiated');

			if (this.oauthService.hasValidAccessToken()) {
				console.log('[AuthService] Revoking tokens and logging out from auth provider');
				await this.oauthService.revokeTokenAndLogout();
				this.clearProviderCookies();
			} else {
				console.log('[AuthService] No active session, redirecting to logout page');
				await this.router.navigate(['auth/logout']);
			}

			this.isDoneLoadingSubject$.next(false);
			this.isAuthenticatedSubject$.next(false);
		} catch (error) {
			console.error('[AuthService] Logout failed:', error);
			await this.router.navigate(['auth/logout']);
		} finally {
			this.clearAuthState();
		}
	}

	/**
	 * Clear Keycloak-specific cookies
	 */
	public clearProviderCookies(): void {
		const cookies = document.cookie.split(';');
		cookies.forEach((cookie: string) => {
			const cookieName = cookie.split('=')[0].trim();

			if (cookieName.includes('AUTH_SESSION_ID') || cookieName.includes('KC_RESTART') || cookieName.includes('KEYCLOAK_')) {
				document.cookie = `${cookieName}=;expires=${new Date(0).toUTCString()};path=/`;
			}
		});
	}

	// ============================================================================
	// TOKEN & USER INFORMATION
	// ============================================================================

	/**
	 * Get all decoded claims details from OAuth service
	 */
	public getOAuthIdentityClaims(): IOpenIDClaims | null {
		try {
			const token = this.oauthService.getAccessToken();

			if (!token) {
				return null;
			}

			const parts = token.split('.');

			if (parts.length !== 3) {
				throw new Error('Invalid JWT token format');
			}

			const base64 = parts[1].replaceAll('-', '+').replaceAll('_', '/');

			let jsonPayload: string;
			try {
				const binaryString = atob(base64);
				const bytes = new Uint8Array(binaryString.length);
				for (let i = 0; i < binaryString.length; i++) {
					bytes[i] = binaryString.charCodeAt(i);
				}
				jsonPayload = new TextDecoder().decode(bytes);
			} catch {
				jsonPayload = decodeURIComponent(escape(atob(base64)));
			}

			return JSON.parse(jsonPayload) as IOpenIDClaims;
		} catch (error) {
			console.error('[AuthService] Failed to decode identity claims:', error);
			return null;
		}
	}

	/**
	 * Returns client-specific roles from Keycloak
	 */
	getOAuthUserClientRoles(clientId: string): string[] {
		const claims = this.getOAuthIdentityClaims();
		return claims?.resource_access?.[clientId]?.roles || [];
	}

	/**
	 * Checks whether a user has a specific client role
	 */
	public hasClientRole(clientId: string, role: string): boolean {
		return this.getOAuthUserClientRoles(clientId).includes(role);
	}

	/**
	 * Get realm-level roles from Keycloak
	 */
	public getRealmRoles(): string[] {
		const claims = this.getOAuthIdentityClaims();
		return claims?.realm_access?.roles || [];
	}

	/**
	 * Check if user has a specific realm role
	 */
	public hasRealmRole(role: string): boolean {
		return this.getRealmRoles().includes(role);
	}

	/**
	 * Get user details from OAuth service
	 */
	public async getOAuthUserDetails(): Promise<{ info: IOpenIDClaims }> {
		try {
			const authUser = await this.oauthService.loadUserProfile() as { info: IOpenIDClaims };

			const roles = this.getOAuthUserClientRoles(this.externOAuth2Provider.config?.client_id);
			authUser.info.resource_access = {
				[this.externOAuth2Provider.config?.client_id]: { roles: roles }
			};

			return authUser;
		} catch (error) {
			console.error('[AuthService] Failed to load user profile:', error);
			throw error;
		}
	}

	/**
	 * Get the current access token
	 */
	public getAccessToken(): string | null {
		return this.oauthService.getAccessToken();
	}

	/**
	 * Get access token as observable
	 */
	public getAccessToken$(): Observable<string | null> {
		return this.isAuthenticated$().pipe(
			map(() => this.getAccessToken())
		);
	}

	/**
	 * Get human-readable time until next refresh
	 */
	public getTimeUntilNextRefresh(): { minutes: number; seconds: number; overdue: boolean } | null {
		const claims = this.getOAuthIdentityClaims();
		if (!claims?.exp || !claims.iat) {
			return null;
		}

		const now = Date.now() / 1000;
		const exp = claims.exp;
		const iat = claims.iat;
		const lifetime = exp - iat;
		const timeoutFactor = this.authCodeFlowConfig?.timeoutFactor || 0.75;

		const refreshAt = iat + (lifetime * timeoutFactor);
		const timeUntilRefresh = refreshAt - now;

		return {
			minutes: Math.floor(Math.abs(timeUntilRefresh) / 60),
			seconds: Math.floor(Math.abs(timeUntilRefresh) % 60),
			overdue: timeUntilRefresh < 0
		};
	}

	// ============================================================================
	// STATE OBSERVABLES
	// ============================================================================

	public isDoneLoadingDocument$(): Observable<boolean> {
		return this.isDoneLoadingSubject$.asObservable();
	}

	public isAuthenticated$(): Observable<boolean> {
		return this.isAuthenticatedSubject$.asObservable();
	}

	public oauthConfigError$(): Observable<boolean> {
		return this.oauthConfigErrorSubject$.asObservable();
	}

	public isOnline$(): Observable<boolean> {
		return this.isOnlineSubject$.asObservable();
	}

	public isCheckingConnection$(): Observable<boolean> {
		return this.isCheckingConnectionSubject$.asObservable();
	}

	// ============================================================================
	// SYNCHRONOUS STATE GETTERS
	// ============================================================================

	public isAuthenticated(): boolean {
		return this.isAuthenticatedSubject$.getValue();
	}

	public isOnline(): boolean {
		return this.isOnlineSubject$.getValue();
	}

	/**
	 * Public API for checking provider availability (components use this)
	 */
	public isProviderAvailable(): boolean {
		return this.isProviderHealthy();
	}

	public isCheckingConnection(): boolean {
		return this.isCheckingConnectionSubject$.getValue();
	}

	public hasOAuthConfigError(): boolean {
		return this.oauthConfigErrorSubject$.getValue();
	}

	public providerIsAuthenticated(): boolean {
		return this.oauthService.hasValidAccessToken();
	}

	// ============================================================================
	// NAVIGATION HELPERS
	// ============================================================================

	private async navigateToAuthCallback(): Promise<void> {
		const authUser = await this.getOAuthUserDetails();
		const state = { oAuthUser: authUser } as IRouteHistoryState;

		await this.router.navigate(['auth/oidc/callback'], { state: state });
	}

	// ============================================================================
	// DEBUG UTILITIES
	// ============================================================================

	/**
	 * Debug method with provider health info
	 * Available via window.debugAuth.debugTokenState() in development
	 */
	public debugTokenState(): void {
		console.log(`%c═══════════════════════════════════════════════`, `color: ${this.LOG_COLORS.INFO};`);

		const token = this.oauthService.getAccessToken();
		const refreshToken = this.oauthService.getRefreshToken();
		const wasAuthenticated = this.isAuthenticatedSubject$.getValue();

		console.log(`%c Basic State:`, `color: ${this.LOG_COLORS.WARNING};`);
		console.table({
			'Authenticated': wasAuthenticated,
			'Has Access Token': !!token,
			'Has Refresh Token': !!refreshToken,
			'Has Valid Token': this.oauthService.hasValidAccessToken(),
			'Is Refreshing': this.isRefreshingToken,
			'Is Online': this.isOnlineSubject$.getValue(),
			'Provider Healthy': this.isProviderHealthy()
		});

		console.log(`%c Retry State:`, `color: ${this.LOG_COLORS.DEBUG};`);
		console.table({
			'Current Attempt': this.retryConfig.currentAttempt,
			'Max Attempts (Auth)': this.retryConfig.maxAttemptsForAuthErrors,
			'Max Attempts (Connection)': this.retryConfig.maxAttemptsForConnectionErrors,
			'Remaining (Connection)': this.retryConfig.maxAttemptsForConnectionErrors - this.retryConfig.currentAttempt
		});

		console.log(`%c Provider Health:`, `color: ${this.LOG_COLORS.SUCCESS};`);
		console.table({
			'State': this.providerHealthTracker.state,
			'Failure Count': this.providerHealthTracker.failureCount,
			'Threshold': this.HEALTH_FAILURE_THRESHOLD,
			'Next Attempt': this.providerHealthTracker.nextAttemptTime > 0
				? new Date(this.providerHealthTracker.nextAttemptTime).toISOString()
				: 'N/A'
		});

		if (!token) {
			console.log('[AuthService] No access token present');
			console.log(`%c═══════════════════════════════════════════════`, `color: ${this.LOG_COLORS.INFO};`);
			return;
		}

		const claims = this.getOAuthIdentityClaims();
		const now = Date.now() / 1000;
		const exp = claims?.exp || 0;
		const iat = claims?.iat || 0;
		const tokenLifetime = exp - iat;
		const timeToExpiry = exp - now;
		const timeoutFactor = this.authCodeFlowConfig?.timeoutFactor || 0.75;
		const refreshScheduledIn = timeToExpiry - (tokenLifetime * (1 - timeoutFactor));

		let refreshStatus: string;
		if (refreshScheduledIn > 60) {
			refreshStatus = `In ${Math.round(refreshScheduledIn / 60)} min`;
		} else if (refreshScheduledIn > 0) {
			refreshStatus = `In ${Math.round(refreshScheduledIn)} sec`;
		} else if (refreshScheduledIn >= -60) {
			refreshStatus = `${Math.abs(Math.round(refreshScheduledIn))} sec ago`;
		} else {
			refreshStatus = `${Math.abs(Math.round(refreshScheduledIn / 60))} min ago`;
		}

		console.log(`%c Token Timing:`, `color: ${this.LOG_COLORS.SUCCESS};`);
		console.table({
			'Token Lifetime (min)': Math.round(tokenLifetime / 60),
			'Time to Expiry (min)': Math.round(timeToExpiry / 60),
			'Refresh Status': refreshStatus,
			'Timeout Factor': `${timeoutFactor * 100}% of lifetime`,
			'Issued At': claims?.iat ? new Date(claims.iat * 1000).toISOString() : 'unknown',
			'Expires At': claims?.exp ? new Date(claims.exp * 1000).toISOString() : 'unknown',
			'Current Time': new Date().toISOString()
		});

		// Check time until next refresh
		const timeUntil = this.getTimeUntilNextRefresh();
		if (timeUntil) {
			console.log(`%c Next Refresh:`, `color: ${this.LOG_COLORS.INFO};`);
			if (timeUntil.overdue) {
				console.warn(` Overdue since ${timeUntil.minutes}m ${timeUntil.seconds}s`);
			} else {
				console.log(` Scheduled in ${timeUntil.minutes}m ${timeUntil.seconds}s`);
			}
		}

		if (timeToExpiry < 60 && refreshScheduledIn < -120) {
			console.warn(`%c WARNING: Token expires soon and refresh is needed!`, `color: ${this.LOG_COLORS.CRITICAL};`);
			console.warn(' Check previous logs for refresh attempts and errors');
		} else if (refreshScheduledIn < 0 && timeToExpiry > 180) {
			console.log(`%c Note: Negative refresh time is normal - token was refreshed`, `color: ${this.LOG_COLORS.INFO}`);
		}

		console.log(`%c═══════════════════════════════════════════════`, `color: ${this.LOG_COLORS.INFO};`);
	}
}
